import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  IconButton,
  Skeleton,
  LinearProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowSharpIcon from '@mui/icons-material/PlayArrowSharp';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import { NowPlayingContext } from '../components/NowPlayingProvider';
import ShowTrackListHeader from '../components/ShowTrackListHeader';
import { SIDENAV_WIDTH } from '../components/LeftSidebar';
import useRetryOnReconnect from '../components/useRetryOnReconnect';

const DESCRIPTION_TRUNCATE_LENGTH = 140;

const ShowTrackList = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = process.env.REACT_APP_WSSR_API_TOKEN;
  const {
    playSpecificTrack,
    pauseTrack,
    currentTrackId,
    isPlaying,
    setIsPlaying,
    setIsLiveStream,
    setNowPlayingData,
    setCurrentTrackIndex,
    setTrackQueue,
  } = useContext(NowPlayingContext);

  const isNowPlaying = (episodeId) => {
    const normalizedId = normalizeTrackId(currentTrackId);
    return normalizedId === episodeId && isPlaying;
  };

  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [trackLengths, setTrackLengths] = useState({});
  const [loadingTrackId, setLoadingTrackId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [buffer, setBuffer] = useState(10);
  const [activeEpisode, setActiveEpisode] = useState(null);

  const startLoadingAnimation = () => {
    setProgress(0);
    setBuffer(10);

    const interval = setInterval(() => {
      setProgress((prev) => Math.min(prev + Math.random() * 10, 100));
      setBuffer((prev) => Math.min(prev + Math.random() * 15, 100));
    }, 200);

    return interval;
  };

  const normalizeTrackId = (trackId) => {
    if (!trackId) return '';
    const parts = trackId.split('/');
    const idWithExtension = parts[parts.length - 1];
    return idWithExtension.replace('.mp3', '');
  };

  const fetchEpisodes = async () => {
    setLoading(true);
    try {
      const endpoint = `https://broadcast.wssr.stream/api/station/1/public/podcast/${id}/episodes`;
      const response = await axios.get(endpoint);
      const publishOrder = [...response.data].sort((a, b) => (a.publish_at || 0) - (b.publish_at || 0));
      const episodeNumbers = new Map(publishOrder.map((episode, i) => [episode.id, i + 1]));
      const enrichedEpisodes = response.data.map((episode, index) => ({
        ...episode,
        index,
        episodeNumber: episodeNumbers.get(episode.id),
        art: episode.art?.startsWith('http')
          ? episode.art
          : `https://broadcast.wssr.stream${episode.art || ''}`,
        path: episode.media?.path || '',
      }));
      setEpisodes(enrichedEpisodes);
      setHasError(false);
    } catch (error) {
      console.error('Error fetching show data:', error);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrackLength = async (episode) => {
    try {
      const trackId = episode.playlist_media?.id;
      if (trackId) {
        const trackresponse = `https://broadcast.wssr.stream/api/station/1/file/${trackId}`;
        const response = await axios.get(trackresponse, { headers: { Authorization: token } });
        setTrackLengths((prev) => ({ ...prev, [episode.id]: response.data.length_text }));
      }
    } catch (error) {
      console.error('Error fetching track length:', error);
    }
  };

  useEffect(() => {
    fetchEpisodes();
  }, [id]);

  const showSkeleton = loading || (hasError && episodes.length === 0);

  useRetryOnReconnect(fetchEpisodes, hasError && episodes.length === 0);

  useEffect(() => {
    episodes.forEach((episode) => {
      if (!episode.media && episode.playlist_media?.id) {
        fetchTrackLength(episode);
      }
    });
  }, [episodes]);

  useEffect(() => {
    if (currentTrackId) {
      const element = document.getElementById(`track-${currentTrackId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentTrackId]);

  const handlePlayPause = async (episode) => {
    if (currentTrackId === episode.id) {
      if (isPlaying) {
        pauseTrack();
      } else {
        setNowPlayingData((prevData) => ({
          ...prevData,
          elapsed: 0,
        }));
        setIsPlaying(true);
      }
    } else {
      setLoadingTrackId(episode.id);
      const loadingAnimation = startLoadingAnimation();

      try {
        const mediaUrl = episode.links?.download;
        if (mediaUrl) {
          const response = await axios.get(mediaUrl, {
            headers: { Authorization: token },
            responseType: 'blob',
          });

          const blobUrl = URL.createObjectURL(response.data);

          playSpecificTrack(blobUrl, {
            title: episode.title || 'Unknown Title',
            artist: episode.description_short || 'Unknown Artist',
            art: episode.art?.startsWith('http')
              ? episode.art
              : `https://broadcast.wssr.stream${episode.art || ''}`,
            duration: episode.duration || 0,
            trackList: episodes.map((ep) => ({ ...ep, source: 'ShowTrackList' })),
          }, episode.id, episode.index);

          setCurrentTrackIndex(episode.index);
          setTrackQueue(episodes);
          setIsLiveStream(false);
          setIsPlaying(true);
        } else {
          console.error('Invalid media URL for episode:', episode);
        }
      } catch (error) {
        console.error('Error during playback:', error);
      } finally {
        clearInterval(loadingAnimation);
        setLoadingTrackId(null);
      }
    }
  };

  const firstEpisode = episodes[0];
  const currentShowEpisode = episodes.find((ep) => ep.id === normalizeTrackId(currentTrackId));
  const isShowPlaying = isPlaying && !!currentShowEpisode;

  return (
    <Box sx={{ flexGrow: 1, paddingTop: '80px', paddingBottom: '130px' }}>
      <IconButton
        onClick={() => navigate(-1)}
        sx={{
          position: 'fixed',
          top: 80,
          left: { xs: 16, md: `${SIDENAV_WIDTH + 24 + 16}px` },
          zIndex: 10,
          backgroundColor: '#1c232f',
          color: '#fff',
          boxShadow: '0 3px 6px rgba(0,0,0,0.1)',
          '&:hover': {
            backgroundColor: '#1c232f',
          },
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      {/* Header Section */}
      <ShowTrackListHeader
        id={id}
        episodeCount={showSkeleton ? 0 : episodes.length}
        onPlayFirst={() => {
          if (isShowPlaying) {
            pauseTrack();
          } else if (currentShowEpisode) {
            setIsPlaying(true);
          } else if (firstEpisode) {
            handlePlayPause(firstEpisode);
          }
        }}
        isPlayingFirst={isShowPlaying}
        loadingFirst={loadingTrackId === (currentShowEpisode?.id ?? firstEpisode?.id)}
      />

      <Box sx={{ maxWidth: '1000px', margin: '0 auto' }}>
        {(showSkeleton ? Array.from(new Array(5)) : episodes).map((episode, index) => {
          const description = episode?.description || '';
          const isTruncated = description.length > DESCRIPTION_TRUNCATE_LENGTH;
          const isActive = episode && isNowPlaying(episode.id);

          return (
            <React.Fragment key={episode?.id || index}>
              {index > 0 && <Divider />}
              <Box
                id={`track-${episode?.id}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: '12px 8px',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                <Box sx={{ flex: '0 0 auto', width: 28, textAlign: 'center' }}>
                  {episode ? (
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {episode.episodeNumber}
                    </Typography>
                  ) : (
                    <Skeleton width={18} height={20} sx={{ margin: '0 auto' }} />
                  )}
                </Box>

                <Box
                  onClick={() => episode && handlePlayPause(episode)}
                  sx={{
                    position: 'relative',
                    flex: '0 0 auto',
                    width: 64,
                    height: 64,
                    cursor: episode ? 'pointer' : 'default',
                  }}
                >
                  {loadingTrackId === episode?.id && (
                    <LinearProgress
                      variant="buffer"
                      value={progress}
                      valueBuffer={buffer}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        zIndex: 1,
                        opacity: progress >= 100 ? 0 : 1,
                        transition: 'opacity 0.3s ease-in-out',
                      }}
                    />
                  )}
                  {episode?.art ? (
                    <img
                      src={episode.art}
                      alt={episode.title || 'Episode Art'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                    />
                  ) : (
                    <Skeleton variant="rectangular" width={64} height={64} sx={{ borderRadius: 1 }} />
                  )}
                  {episode && (
                    isActive ? (
                      <EqualizerIcon
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: 'white',
                          opacity: 0.9,
                        }}
                      />
                    ) : (
                      <PlayArrowSharpIcon
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: 'white',
                          opacity: 0.9,
                        }}
                      />
                    )
                  )}
                </Box>

                <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                  {episode?.title ? (
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 'bold',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {episode.title}
                    </Typography>
                  ) : (
                    <Skeleton width="50%" height={24} />
                  )}

                  {episode?.description ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#999',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {description}
                    </Typography>
                  ) : episode ? null : (
                    <Skeleton width="80%" height={18} />
                  )}

                  {isTruncated && (
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveEpisode(episode);
                      }}
                      sx={{ padding: 0, minWidth: 0, fontSize: '0.75rem' }}
                    >
                      More
                    </Button>
                  )}

                  {isActive ? (
                    <Box display="flex" alignItems="center" mt={0.5}>
                      <EqualizerIcon sx={{ fontSize: '14px', mr: 0.5, color: 'primary.main' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        Now Playing
                      </Typography>
                    </Box>
                  ) : episode?.media?.length_text || trackLengths[episode?.id] ? (
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.75rem' }}>
                      {episode.media?.length_text || trackLengths[episode.id]}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </React.Fragment>
          );
        })}
      </Box>

      <Dialog open={!!activeEpisode} onClose={() => setActiveEpisode(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {activeEpisode?.title}
          <IconButton onClick={() => setActiveEpisode(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="textSecondary">
            {activeEpisode?.description}
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ShowTrackList;
